import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { axiosApi } from '../../../axios-api';
import { resetGraphicEditorRouteState } from '../bootstrap/resetGraphicEditorRouteState';

/**
 * Route-scoped load: reset stores, fetch project/design, optional org images/fonts.
 * Uses AbortController on project_id / design_id changes.
 */
export function useGraphicEditorBootstrap({
  api,
  projectId,
  designId,
  setProject,
  setRuleId,
  setDesign,
  setUserImages,
  setStage,
  createStage,
}) {
  const getImages = useCallback(
    async (organizationId, signal) => {
      try {
        const { data } = await axiosApi.get(`v2/projects/files/organization/${organizationId}`);
        if (signal?.aborted) return;
        const mergedImages = [
          ...data.uploaded_images.map((img) => ({ ...img, isCustom: true })),
          ...data.default_images.map((img) => ({
            ...img,
            isCustom: false,
            id: Math.floor(Math.random() * 1000),
          })),
        ];
        setUserImages(mergedImages);
      } catch (error) {
        if (error.isAxiosError) message.warning({ content: error.message, duration: 3 });
        else throw new Error(error);
      }
    },
    [setUserImages],
  );

  const loadDesign = useCallback(
    async (signal) => {
      try {
        const designData = await api.fetchDesign(projectId, designId);
        if (signal.aborted) return;
        if (!designData) return;
        await api.setDesignFont(designData.fonts);
        if (signal.aborted) return;
        setDesign(designData);
      } catch (error) {
        if (!signal.aborted) {
          message.error(error?.message ?? error);
        }
      }
    },
    [api, projectId, designId, setDesign],
  );

  const loadProject = useCallback(
    async (pid, signal) => {
      try {
        const result = await api.fetchProject(pid);
        if (signal.aborted) return;
        if (!result) return;

        const orgId = result?.organization?.id;
        const foundOrganization = api?.user?.roles?.find(
          (role) => Number(role.organization) === Number(orgId),
        );
        let newProject = result;
        if (foundOrganization) {
          if (foundOrganization.generation_allowed === false) {
            message.warning({
              content:
                'Generation is limited for this organization. You can still edit the design; run/export may be restricted.',
              duration: 5,
            });
          }
          newProject = { ...result, organization: { ...result.organization, ...foundOrganization } };
        }
        if (signal.aborted) return;
        setProject(newProject);
        setRuleId(result?.rule?.id);

        if (foundOrganization) {
          const orgIdForFetch = foundOrganization.organization;
          void (async () => {
            try {
              await getImages(orgIdForFetch, signal);
              if (signal.aborted) return;
              await api.getFonts(orgIdForFetch);
            } catch (e) {
              if (!signal.aborted) {
                message.warning({
                  content: e?.message ?? String(e),
                  duration: 4,
                });
              }
            }
          })();
        }
      } catch (err) {
        if (!signal.aborted) {
          message.error(err?.message ?? err);
        }
      }
    },
    [api, getImages, setProject, setRuleId],
  );

  const runGraphicInit = useCallback(
    async (signal) => {
      resetGraphicEditorRouteState();
      if (signal.aborted) return;
      await loadProject(projectId, signal);
      if (signal.aborted) return;
      if (designId) {
        await loadDesign(signal);
      } else {
        if (signal.aborted) return;
        setStage(createStage());
      }
    },
    [projectId, designId, loadProject, loadDesign, setStage, createStage],
  );

  const runGraphicInitRef = useRef(runGraphicInit);
  runGraphicInitRef.current = runGraphicInit;

  const initProject = useCallback(async () => {
    const ac = new AbortController();
    await runGraphicInit(ac.signal);
  }, [runGraphicInit]);

  useEffect(() => {
    const ac = new AbortController();
    void runGraphicInitRef.current(ac.signal);
    return () => ac.abort();
  }, [projectId, designId]);

  return { initProject, getImages };
}
